import React, { useState, useEffect } from 'react';
import { Grid, Typography, Container, Box, useTheme, CircularProgress, Alert } from '@mui/material';
import { motion } from 'framer-motion';
import ProgramShowcase from '../sections/ProgramShowcase';
import PageHeader from '../ui/PageHeader';

const Programs = () => {
  const theme = useTheme();
  const [programsData, setProgramsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetch('/data/levels.json')
      .then(response => {
        if (!response.ok) {
          throw new Error('Failed to load programs data');
        }
        return response.json();
      })
      .then(data => {
        setProgramsData(data.programs.filter(program => program.active));
        setLoading(false);
      })
      .catch(err => {
        console.error('Error loading programs:', err);
        setError('Failed to load programs data. Please try again later.');
        setLoading(false);
      });
  }, []);

  return (
    <Container sx={{ mt: 4, mb: 4 }}>
      <PageHeader
        title="Our Programs"
        subtitle="We offer various levels to suit swimmers of all abilities."
      />

      {/* Loading State */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Error State */}
      {error && (
        <Alert severity="error" sx={{ mb: 4 }}>
          {error}
        </Alert>
      )}

      {/* Programs Grid */}
      {!loading && !error && (
        <Box
          component={motion.div}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-60px' }}
          transition={{ duration: 0.5 }}
        >
        <Grid container spacing={4}>
          {programsData.map((program) => (
            <Grid item xs={12} sm={6} md={4} key={program.id}>
              <Box
                sx={{
                  borderRadius: '20px',
                  background: theme.palette.background.paper,
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  color: theme.palette.text.primary,
                  boxShadow: '0px 8px 32px rgba(0,0,0,0.3)',
                  transition: 'transform 0.4s cubic-bezier(0.16, 1, 0.3, 1), box-shadow 0.4s ease',
                  p: 3,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: theme.palette.mode === 'dark' ? '0px 15px 40px rgba(0,0,0,0.7)' : '0px 15px 40px rgba(0,0,0,0.2)',
                  }
                }}
              >
                <Typography variant="h4" sx={{ fontWeight: 'bold', textAlign: 'center' }}>
                  {program.level}
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 500, opacity: 0.9, textAlign: 'center', mb: 0.5 }}>
                  {program.stroke}
                </Typography>
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.65, textAlign: 'center' }}>
                  {program.description}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
        </Box>
      )}


      <Box
        component={motion.div}
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-60px' }}
        transition={{ duration: 0.5 }}
        sx={{ textAlign: 'center', mb: 4, pt: 4 }}
      >
        <Typography variant="h4" gutterBottom sx={{ color: theme.palette.text.primary, fontWeight: 700 }}>
          Our Current Schedule
        </Typography>
        <Box sx={{ width: 36, height: 3, bgcolor: theme.palette.primary.main, mx: 'auto', mt: 0.5, borderRadius: 1.5 }} />
      </Box>
      <ProgramShowcase />
    </Container>
  );
};

export default Programs;
